import React, { useEffect, useState } from 'react';
import { Steps } from 'antd';
import { STEPS_STATUS } from '../../shared/Constants';

interface Step {
	title: string;
	description?: string | null;
	progress?: number;
}

interface StepsVerticalProps {
	steps: Array<Step>;
	activeStep: number;
	onNext: () => void | Promise<boolean>;
	onPrevious: () => void | Promise<boolean>;
	onStepChange: (step: number) => void;
	disableSteps?: boolean;
	currentWizard: number;
	totalStepsInPreviousWizard: number;
	draftIndex?: number;
	completedDraftIndex?: number;
	isProposerAndViewable?: boolean;
}

const StepsVertical: React.FC<StepsVerticalProps> = ({
	steps,
	activeStep,
	onStepChange,
	currentWizard,
	totalStepsInPreviousWizard,
	draftIndex,
	disableSteps,
	completedDraftIndex,
	isProposerAndViewable = false,
}) => {
	const [completedSteps, setCompletedSteps] = useState<
		Record<number, boolean[]>
	>({});

	useEffect(() => {
		if (isProposerAndViewable) return;
		setCompletedSteps((prev) => {
			const updatedSteps = { ...prev };
			if (
				!updatedSteps[currentWizard] ||
				updatedSteps[currentWizard].length !== steps.length
			) {
				updatedSteps[currentWizard] = Array(steps.length).fill(
					false
				) as boolean[];
			}

			let presentWizard = 0;

			if (draftIndex && completedDraftIndex) {
				if (currentWizard && draftIndex >= 7) {
					presentWizard = 1;
				}

				// Ensure presentWizard array is initialized
				if (!updatedSteps[presentWizard]) {
					updatedSteps[presentWizard] = Array(steps.length).fill(
						false
					) as boolean[];
				}

				if (!presentWizard) {
					for (let i = 0; i <= completedDraftIndex; i++) {
						updatedSteps[presentWizard][i] = true;
					}
				} else {
					if (!updatedSteps[presentWizard - 1]) {
						updatedSteps[presentWizard - 1] = Array(steps.length).fill(
							false
						) as boolean[];
					}

					for (let i = 0; i <= completedDraftIndex; i++) {
						if (i <= 6) {
							updatedSteps[presentWizard - 1][i] = true;
						} else {
							updatedSteps[presentWizard][completedDraftIndex - i] = true;
						}
					}
				}
			}

			if (prev[currentWizard]) {
				for (let i = 0; i < prev[currentWizard].length; i++) {
					if (prev[currentWizard][i]) {
						updatedSteps[currentWizard][i] = true;
					}
				}
			}

			if (!draftIndex) {
				for (let i = 0; i < activeStep; i++) {
					updatedSteps[currentWizard][i] = true;
				}
			}

			return updatedSteps;
		});
	}, [
		currentWizard,
		activeStep,
		steps.length,
		totalStepsInPreviousWizard,
		draftIndex,
		completedDraftIndex,
		isProposerAndViewable,
	]);

	const wizardCompletedSteps =
		completedSteps[currentWizard] || Array(steps.length).fill(false);

	const stepsDisableCheck =
		!isProposerAndViewable &&
		(disableSteps || (currentWizard === 1 && activeStep === 0));

	return (
		<div className="w-full">
			<Steps
				current={activeStep}
				direction="vertical"
				onChange={(step) => {
					if (stepsDisableCheck) {
						return;
					}
					if (
						isProposerAndViewable ||
						wizardCompletedSteps[step] ||
						step < activeStep
					) {
						onStepChange(step);
					}
				}}
				className="mb-4 space-y-6"
			>
				{steps.map((step, index) => (
					<Steps.Step
						key={index}
						title={
							<div
								className={`flex items-center gap-2 ${
									stepsDisableCheck ? 'cursor-not-allowed' : 'cursor-pointer'
								}`}
							>
								<div>
									<strong
										className={`${
											!isProposerAndViewable &&
											(disableSteps ||
												(currentWizard === 1 && activeStep === 0))
												? 'text-gray-400'
												: wizardCompletedSteps[index] || index < activeStep
												? 'text-primary-blue'
												: index < activeStep
												? 'text-primary-blue'
												: index === activeStep
												? 'text-primary-blue'
												: 'text-gray-500'
										}`}
									>
										{step.title}
									</strong>
									<p className="m-0 text-sm text-gray-600 min-h-[3.5rem]">
										{step.description || ''}
									</p>
								</div>
							</div>
						}
						status={
							isProposerAndViewable
								? index === activeStep
									? STEPS_STATUS.PROCESS
									: STEPS_STATUS.WAIT
								: wizardCompletedSteps[index] || index < activeStep
								? STEPS_STATUS.FINISH
								: index === activeStep
								? STEPS_STATUS.PROCESS
								: STEPS_STATUS.WAIT
						}
						disabled={
							!isProposerAndViewable &&
							!wizardCompletedSteps[index] &&
							index > activeStep
						}
					/>
				))}
			</Steps>
		</div>
	);
};

export default StepsVertical;
